import type {
  ApprovedAvatar,
  ApprovedSet
} from '../storage/approvedVerificationStorage';

export const BACKUP_VERSION = 1;

// Parses + validates a raw backup file JSON string. Throws with a user-facing message
// on any shape mismatch. Shared between web (File.text() input) and mobile
// (FileSystem.readAsStringAsync input) so both platforms reject the same bad files.
export function parseApprovedBackup(
  raw: string,
  expectedUserId: number
): ApprovedSet {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Backup file is not valid JSON.');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Backup file is malformed.');
  }
  const p = parsed as {
    version?: unknown;
    userId?: unknown;
    approvedBios?: unknown;
    approvedAvatars?: unknown;
  };
  if (p.version !== BACKUP_VERSION) {
    throw new Error(
      `Backup version mismatch (got ${String(p.version)}, expected ${BACKUP_VERSION}).`
    );
  }
  if (p.userId !== expectedUserId) {
    throw new Error(
      `This backup belongs to a different account (user ${p.userId}).`
    );
  }
  const bios = p.approvedBios;
  const avatars = p.approvedAvatars;
  if (!Array.isArray(bios) || !bios.every((b) => typeof b === 'string')) {
    throw new Error('Backup is missing valid approvedBios.');
  }
  if (
    !Array.isArray(avatars) ||
    !avatars.every(
      (a): a is ApprovedAvatar =>
        !!a &&
        typeof a === 'object' &&
        typeof (a as { id?: unknown }).id === 'string' &&
        typeof (a as { bytes?: unknown }).bytes === 'string'
    )
  ) {
    throw new Error('Backup is missing valid approvedAvatars.');
  }
  return { approvedBios: bios as string[], approvedAvatars: avatars };
}
